import { Location } from '@angular/common';
import { Component, OnInit, Inject, Input, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from 'src/app/shared/utils.service';
import { ActionModel } from '../action.model';
import { ActionsService } from '../actions.service';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { FilesRemoveComponent } from 'src/app/files/files-remove/files-remove.component';
import { NetworkedUsersSelectorComponent } from 'src/app/shared/networked-users-selector/networked-users-selector.component';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from 'src/app/shared/rbac.service';
import moment from 'moment';

@Component({
    selector: 'app-actions-edit',
    templateUrl: './actions-edit.component.html',
    styleUrls: ['./actions-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ActionsEditComponent implements OnInit {
  @Input() action: ActionModel;
  @Input() show_title = true;

  types = this.utils.getActionTypes();
  nonSystemOrigins = this.utils.getActionNonSystemOrigins();
  systemOrigins = this.utils.getActionSystemOrigins();
  isSystemOrigin = false;
  priorities = ['Low', 'Medium', 'High', 'Critical'];
  now = new Date();
  inProgress = false;

  selectedDate: moment.Moment;

  selectedFiles: File[] = [];

  // RBAC and plan feature flags. Resolved once in ngOnInit() so the template does
  // not re-evaluate these checks on every change detection cycle.
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  hasSitesPlanFeature: boolean = false;

  constructor(
    public app: AppService,
    public location: Location,
    public utils: UtilsService,
    private actionsService: ActionsService,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: ActionModel,
    private dialogRef: MatDialogRef<ActionsEditComponent>,
    private api: ApiRequestService,
    public rbac: RBACService,
  ) {}

  ngOnInit() {
    // Resolve RBAC and plan feature flags before any early return below.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_DELETE)
    ], true);
    this.hasSitesPlanFeature = this.app.planCan(AppService.FEATURES.SITES);

    if (!this.action && this.dialogData) {
      this.action = this.dialogData;
    }

    this.isSystemOrigin = (this.dialogData as any)?.is_system_origin === true || this.systemOrigins.includes(this.action?.origin);

    if ( !this.action.id ) { return (this.action.priority = 'Medium'); }
    this.find();
  }

  onDateChange(value) {
    this.selectedDate = value;
  }

  onSubmit() {

    // Layer 2: RBAC guard.
    if (this.action.id) {
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_EDIT)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit tasks.');
        return;
      }
    } else {
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_CREATE)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to create tasks.');
        return;
      }
    }

    if (this.action.user_ids == null || !this.action.user_ids.length) {
      this.utils.showModal(
        'Assignees Required',
        'Please add at least one ' + this.utils.getLangTerm('employees.singular', 'Employee') + ' as an assignee'
      );
      return;
    }

    if ( this.inProgress ) {
      return;
    }

    this.inProgress = true;

    // Check if a due date was selected before attempting to use formatting functions.
    if ( this.selectedDate ) {
      this.action.due = this.selectedDate.format('YYYY-MM-DD');
    }

    if ( this.action.id ) {
      this.update();
    } else {
      this.create();
    }
  }

  private find() {
    this.actionsService.find(this.action.id).subscribe((result) => {
      this.action = result.data;
      this.action.user_ids = this.action.users.map((user) => user.id);
    });
  }

  onRemoveFiles() {
    const dialogRef = this.dialog.open(FilesRemoveComponent, {
      data: this.action.files,
      width: '700px'
    });
    dialogRef.afterClosed().subscribe(() => this.find());
  }

  selectAssignee() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.action.user_ids,
        multiple: true,
        selectedAccountId: this.app.account.id,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) { return; }
        this.action.user_ids = results;
      }
    );
  }

  onSelectSite() {
    const dialogRef = this.dialog.open(SitesSelectorComponent, {
      width: '1200px',
      data: { multiple: false, selected: [this.action.site_id] }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (typeof result !== 'number') { return; }
      this.action.site_id = result;
    });
  }

  delete() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete tasks.');
      return;
    }

    this.utils.showModal(
      'Remove Actions',
      `Are you sure you want to remove "${this.action.name}"?`,
      () => {
        this.actionsService.remove([this.action.id]).subscribe(() => {
          this.utils.showToast(`"${this.action.name}" has been deleted.`);
          this.dialogRef.close(true);
        });
      }
    );
  }

  /**
   * Updates the current action by making a PUT request to the specified endpoint.
   * Handles the success response by invoking success and file upload methods.
   * Resets the in-progress state upon completion of the update process.
   *
   * @return {void} No value is returned as the method performs side effects like making an API call and handling the response.
   */
  private update(): void {
    this.api
      .makeRequest('put', `v1/actions/${this.action.id}`, this.action)
      .then((response: { data: ActionModel }): void => {
        this.onSuccess(`Updated: "${response.data.name}"`);
        this.find();
      })
      .finally((): void => {
        this.inProgress = false;
      });
  }

  /**
   * Sends a POST request to create a new action and handles the response.
   *
   * @return {void} This method does not return a value.
   */
  private create(): void {
    this.api
      .makeRequest('post', 'v1/actions', this.action)
      .then((response: { data: ActionModel; meta: {total: number} }): void => {
        this.onSuccess(`Created ${response.meta.total} Task(s)`, true);
        // related_action_ids contains the primary task id.
        if ( typeof response?.data?.related_action_ids !== 'undefined' && response?.data?.related_action_ids?.length > 0 ) {
          for ( let i: number = 0; i < response.data.related_action_ids.length; i++ ) {
            this.uploadSelectedFiles(response.data.related_action_ids[i], null, (i === response.data.related_action_ids.length - 1));
          }
        }
      })
      .finally((): void => {
        this.inProgress = false;
      });
  }

  /**
   * Handles the successful execution of an operation by displaying a message
   * and optionally closing a dialog.
   *
   * @param {string} message - The success message to be displayed in a toast notification.
   * @param {boolean} [shouldCloseDialog=false] - Optional flag indicating whether the dialog should be closed. Defaults to false.
   * @return {void} This method does not return any value.
   */
  private onSuccess(message: string, shouldCloseDialog: boolean = false) : void {
    this.utils.showToast(message);
    if ( shouldCloseDialog ) {
      this.dialogRef.close(true);
    }
  }

  /**
   * Handles the selection of files by appending them to the existing selected files array.
   *
   * @return {void} - Does not return any value.
   * @param fileList
   */
  onSelectFiles(fileList: FileList): void {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }

  /**
   * Uploads the selected files to the server end-point associated with the given ID.
   * Displays a success toast message upon successful upload and executes a callback if provided.
   *
   * @param {number} id - The identifier used to create the upload endpoint.
   * @param {Function} [callback] - An optional callback function to be executed after the upload process is completed.
   * @param isLast - If it's the last task to upload files for, we need to clear the selected files array.
   * @return {void} - This method does not return any value.
   */
  private uploadSelectedFiles(id: number, callback?: any, isLast: boolean = false): void {
    if ( id && this.selectedFiles.length > 0 ) {
      this.api.makeUploadRequest(`v2/file-manager/action/${id}`, this.selectedFiles)
        .then((response: any): void => {
          if ( isLast ) {
            this.utils.showToast('Your files successfully uploaded.');
          }
        })
        .finally((): void => {
          if ( isLast ) {
            this.selectedFiles.length = 0;
          }
          if ( typeof callback === 'function' ) {
            callback();
          }
        });
    }
  }

  // Expose RBACService and AppService to template.
  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}
